import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  Award,
  Coins,
  Gavel,
  Plus,
  Receipt,
  Repeat,
  Rocket,
  Tag,
  type LucideIcon,
} from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'

import ScreenLayout from '@/components/layout/ScreenLayout'
import BackButton from '@/components/ui/BackButton'
import Button from '@/components/ui/Button'
import Chip from '@/components/ui/Chip'
import EmptyState from '@/components/ui/EmptyState'
import NotAvailable from '@/components/ui/NotAvailable'
import Skeleton from '@/components/ui/Skeleton'
import TextLink from '@/components/ui/TextLink'
import { toast } from '@/components/ui/toast-store'
import { failureMessage, portfolioFailure } from '@/features/portfolio/api/errors'
import { mitraFieldNotes, type TradeFill } from '@/features/portfolio/tradeText'
import handsUpArt from '@/assets/mitra-3d/hands-up.png'
import mitraHead from '@/assets/mitra-3d/mark.png'
import { cn } from '@/lib/utils'
import type { ImportPreview } from '@/schemas/portfolio'
import ContractNoteCard from '@/screens/portfolio/components/add-ai/ContractNoteCard'
import ReadingSteps, { type ReadStep } from '@/screens/portfolio/components/add-ai/ReadingSteps'
import RecentEntries from '@/screens/portfolio/components/add-ai/RecentEntries'
import ShareholderChip from '@/screens/portfolio/components/add-ai/ShareholderChip'
import SipForm, { SIP_AVAILABLE } from '@/screens/portfolio/components/add-transaction/SipForm'
import TransactionForm, {
  type FieldNotes,
} from '@/screens/portfolio/components/add-transaction/TransactionForm'
import { rateTableLabel } from '@/screens/portfolio/components/add-transaction/costPreview'
import {
  TRANSACTION_KINDS,
  kindOption,
  type TransactionKindValue,
} from '@/screens/portfolio/components/add-transaction/kinds'
import { useTradeReading } from '@/screens/portfolio/components/add-transaction/useTradeReading'
import { useTransactionEntry } from '@/screens/portfolio/components/add-transaction/useTransactionEntry'
import { companyLabel } from '@/screens/portfolio/components/add-transaction/useSymbolSearch'

export const Route = createFileRoute('/_app/portfolio/add/ai')({ component: AddWithMitraScreen })

/** The example the composer offers, and the error copy quotes (mock `bj`). */
const EXAMPLE = 'bought 100 NABIL at 498 on 2 Bhadra'

/** Each tab's glyph, as the mock's chips draw them (`Yhe`). */
const KIND_ICON: Record<TransactionKindValue | 'sip', LucideIcon> = {
  buy: Plus,
  sell: Receipt,
  cashDividend: Coins,
  bonusDividend: Award,
  ipo: Rocket,
  fpo: Rocket,
  rights: Tag,
  auction: Gavel,
  sip: Repeat,
}

/** The four reading steps, then a beat before the form (mock `kO` × 4 + 380ms). */
const STEP_COUNT = 4
const SETTLE_STEPS = 0.6

type Tab = TransactionKindValue | 'sip'

/**
 * What the page is showing. `input`: the composer. `reading`: the steps, while the message is
 * read. `form`: the form — `from` is the message Mitra filled it from (the banner), or `null` when
 * the member picked a type and fills it by hand.
 */
type Stage =
  | { kind: 'input' }
  | { kind: 'reading'; text: string; fill: TradeFill | null }
  | { kind: 'form'; from: string | null; notes: FieldNotes; fill: TradeFill | null }

/**
 * Add transaction · Mitra AI (`/portfolio/add/ai`, mock `Khe`, COMPARE-pf1 §4; owner P8): tell
 * Mitra what you did, watch it read, check the filled form, save. Typed text is read by the same
 * client parser as the dialog's "Fill the form" box, and a pasted broker SMS by mm-be's own
 * (`/import/preview/` `portal=sms`) — `useTradeReading`. The form and its writes are the dialog's
 * (`TransactionForm`, `useTransactionEntry`). Uploading a contract note waits on an endpoint
 * (`ContractNoteCard`, P7).
 *
 * ```
 * input ──Read message──▶ reading (4 steps) ──▶ form (banner + warning edges) ──Save──▶ input
 *   │                          └─Cancel─▶ input            └─Edit message─▶ input
 *   └──a type tab──▶ form (by hand)
 * ```
 *
 * Full width, no rail, the section strip kept with Activity lit (the mock's `sideActive`).
 */
function AddWithMitraScreen() {
  const entry = useTransactionEntry()
  const { view, profile } = entry
  const reading = useTradeReading({ profile, scopeId: entry.scopeId })

  return (
    <ScreenLayout
      fullWidth
      pageTitle="Add transaction"
      header={
        <div className="flex min-h-9 flex-wrap items-center gap-x-4 gap-y-2">
          <BackButton to="/portfolio/activity" />
          <h1 className="text-h-section text-foreground">Add transaction</h1>
          <span className="flex-1" />
          {profile === undefined || entry.shareholders.data === undefined ? null : (
            <ShareholderChip profiles={entry.shareholders.data} active={profile} />
          )}
        </div>
      }
    >
      <div className="flex max-w-360 flex-col gap-6">
        {view === 'loading' ? (
          <div aria-busy="true" className="flex flex-col gap-4">
            <span className="sr-only">Loading your profiles…</span>
            <Skeleton appear className="h-4 w-80" />
            <Skeleton appear className="h-75 w-full" />
          </div>
        ) : view === 'failed' || view === 'offline' ? (
          <EmptyState
            kind={view}
            size="page"
            {...(view === 'failed'
              ? {
                  description: failureMessage(
                    portfolioFailure(entry.shareholders.error) ?? { kind: 'failed' },
                  ),
                }
              : {})}
            onRetry={() => {
              void entry.shareholders.refetch()
            }}
            retrying={entry.shareholders.isFetching}
          />
        ) : view === 'empty' || profile === undefined ? (
          <EmptyState
            kind="empty"
            size="page"
            title="Create a portfolio profile first"
            description="Transactions are recorded against a shareholder profile — the demat account the trade settled in. Add one from Portfolio, then come back to record this entry."
          />
        ) : (
          <MitraEntry entry={entry} reading={reading} />
        )}
        {view === 'loaded' && profile !== undefined ? (
          <RecentEntries scopeId={entry.scopeId} />
        ) : null}
      </div>
    </ScreenLayout>
  )
}

interface MitraEntryProps {
  entry: ReturnType<typeof useTransactionEntry>
  reading: ReturnType<typeof useTradeReading>
}

function MitraEntry({ entry, reading }: MitraEntryProps) {
  const formId = useId()
  const composerId = useId()
  const errorId = `${composerId}-error`
  const composer = useRef<HTMLTextAreaElement>(null)
  const navigate = useNavigate()

  const [stage, setStage] = useState<Stage>({ kind: 'input' })
  const [text, setText] = useState('')
  const [error, setError] = useState('')
  const [many, setMany] = useState<ImportPreview | null>(null)
  const [sip, setSip] = useState(false)
  const [reached, setReached] = useState(0)
  const [flash, setFlash] = useState<{ key: number; fill: TradeFill | null }>({
    key: 0,
    fill: null,
  })

  const formStage = stage.kind === 'form' ? stage : null

  // The steps tick on while the message is read; the form fills once both are done.
  useEffect(() => {
    if (stage.kind !== 'reading') return
    const timers = Array.from({ length: STEP_COUNT }, (_, index) =>
      window.setTimeout(
        () => {
          setReached(index + 1)
        },
        stepMs() * (index + 1),
      ),
    )
    return () => {
      timers.forEach((timer) => {
        window.clearTimeout(timer)
      })
    }
  }, [stage.kind])

  /** Bumped by every read and by Cancel, so a read that was cancelled lands nowhere. */
  const readToken = useRef(0)

  const read = async () => {
    const said = text.trim()
    setMany(null)
    if (said === '') {
      setError(`Tell Mitra what you did first. For example: ${EXAMPLE}.`)
      composer.current?.focus()
      return
    }
    setError('')
    readToken.current += 1
    const mine = readToken.current
    const started = performance.now()
    setReached(0)
    setStage({ kind: 'reading', text: said, fill: null })
    const result = await reading.read(said)
    if (readToken.current !== mine) return
    if (result.kind === 'fill') {
      const { fill } = result
      setStage({ kind: 'reading', text: said, fill })
      // The form fills once the four steps have played, however fast the read was.
      const left = stepMs() * (STEP_COUNT + SETTLE_STEPS) - (performance.now() - started)
      if (left > 0) {
        await new Promise((done) => {
          window.setTimeout(done, left)
        })
      }
      if (readToken.current !== mine) return
      entry.applyFill(fill)
      setSip(false)
      setFlash((last) => ({ key: last.key + 1, fill }))
      setStage({ kind: 'form', from: said, notes: mitraFieldNotes(fill), fill })
      return
    }
    setStage({ kind: 'input' })
    if (result.kind === 'many') {
      setMany(result.preview)
      setError(
        `${String(result.preview.rows.length)} trades found in that message. Check them together in Import.`,
      )
    } else if (result.kind === 'failed') setError(result.message)
    else if (result.fill?.sip === true) setError('SIP isn’t supported yet.')
    else setError('Mitra could not read a trade in that.')
    composer.current?.focus()
  }

  const backToMessage = () => {
    readToken.current += 1
    setStage({ kind: 'input' })
    requestAnimationFrame(() => composer.current?.focus())
  }

  const pickTab = (tab: Tab) => {
    setSip(tab === 'sip')
    if (tab !== 'sip') entry.switchKind(tab)
    if (stage.kind === 'form') return
    readToken.current += 1
    setFlash((last) => ({ key: last.key, fill: null }))
    setStage({ kind: 'form', from: null, notes: {}, fill: null })
  }

  const save = () => {
    entry.save(({ entry: line }) => {
      const label = kindOption(entry.kind).noun
      toast.success(
        `${label} saved to ${entry.profile?.name ?? 'your portfolio'}: ${line.detail}`,
        {
          label: 'View in Activity',
          onClick: () => {
            void navigate({ to: '/portfolio/activity' })
          },
        },
      )
      entry.clearEntry()
      setText('')
      setFlash((last) => ({ key: last.key, fill: null }))
      setStage({ kind: 'input' })
    })
  }

  /** Notes stay only while the field still holds what Mitra put there (mock: `Qhe` / equality). */
  const notes: FieldNotes = {}
  if (formStage !== null && formStage.fill !== null) {
    const { fill } = formStage
    const said = formStage.notes
    if (said.date !== undefined && (fill.date === null || entry.adDate === fill.date.ad))
      notes.date = said.date
    if (said.price !== undefined && entry.price === '') notes.price = said.price
    if (said.company !== undefined && entry.company === null) notes.company = said.company
  }

  const steps: readonly ReadStep[] = readSteps(
    stage.kind === 'reading' ? stage.fill : null,
    entry.adDate,
  )
  const tabs: readonly { value: Tab; label: string }[] = [
    ...TRANSACTION_KINDS.map(({ value, label }) => ({ value, label })),
    { value: 'sip', label: 'SIP' },
  ]

  return (
    <div className="flex flex-col gap-3">
      {formStage?.from === null || formStage === null ? null : (
        <div className="flex items-center gap-3 border border-border-subtle bg-card py-3 pr-5 pl-4 max-sm:flex-wrap">
          <img src={mitraHead} alt="" className="size-8 shrink-0 rounded-full object-contain" />
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <p className="text-h-s text-foreground">Mitra filled this from your message</p>
            <p className="text-body-m text-muted-foreground">
              “{formStage.from.replace(/\.?$/, '.')}” Check each field, then save. A warning edge
              marks a field Mitra was not sure about.
            </p>
          </div>
          <TextLink onClick={backToMessage}>Edit message</TextLink>
        </div>
      )}

      <p className="text-body-xs text-muted-foreground-subtle">
        Nothing is written until you save, and history is never deleted.
      </p>

      <div className="flex flex-col gap-4 bg-card px-5 py-4">
        <div role="tablist" aria-label="Transaction type" className="flex flex-wrap gap-2">
          <Chip
            role="tab"
            aria-selected={stage.kind !== 'form'}
            variant="subtab"
            active={stage.kind !== 'form'}
            onClick={() => {
              if (stage.kind === 'form') backToMessage()
            }}
            className={chipSkin(stage.kind !== 'form')}
          >
            <img src={handsUpArt} alt="" className="size-5 rounded-full object-contain" />
            Mitra AI
          </Chip>
          {tabs.map((tab) => {
            const on =
              stage.kind === 'form' &&
              (tab.value === 'sip' ? sip : !sip && entry.kind === tab.value)
            const Icon = KIND_ICON[tab.value]
            return (
              <Chip
                key={tab.value}
                role="tab"
                aria-selected={on}
                variant="subtab"
                active={on}
                onClick={() => {
                  pickTab(tab.value)
                }}
                className={chipSkin(on)}
              >
                <Icon aria-hidden="true" className="size-4" />
                {tab.label}
              </Chip>
            )
          })}
        </div>
        <span aria-hidden="true" className="h-px w-full bg-border-subtle" />

        {stage.kind === 'input' ? (
          <div className="flex gap-5 max-lg:flex-col">
            <div className="flex min-w-0 flex-1 flex-col gap-3">
              <div className="flex items-center gap-2">
                <img src={mitraHead} alt="" className="size-8 rounded-full object-contain" />
                <div className="flex flex-col gap-px">
                  <label htmlFor={composerId} className="text-h-m text-foreground">
                    Tell Mitra what you did
                  </label>
                  <p className="text-body-xs text-muted-foreground-subtle">
                    Mitra fills the form. You check it before saving.
                  </p>
                </div>
              </div>
              <textarea
                id={composerId}
                ref={composer}
                value={text}
                placeholder={`For example: ${EXAMPLE.toLowerCase()}`}
                aria-invalid={error === '' ? undefined : true}
                aria-describedby={error === '' ? undefined : errorId}
                onChange={(event) => {
                  setText(event.target.value)
                  if (error !== '') setError('')
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) void read()
                }}
                className={cn(
                  'h-28 resize-none border bg-muted p-3 text-body-l text-foreground transition-colors outline-none placeholder:text-muted-foreground-subtle hover:border-border focus-visible:border-ring focus-visible:ring-1 focus-visible:ring-ring lg:text-body-m',
                  error === ''
                    ? 'border-transparent'
                    : 'border-destructive hover:border-destructive',
                )}
              />
              <div className="flex min-h-9 items-center gap-2">
                {error === '' ? (
                  <p className="min-w-0 flex-1 text-body-xs text-muted-foreground-subtle">
                    {text.trim() === '' ? '' : '⌘ Enter to read'}
                  </p>
                ) : (
                  <p
                    id={errorId}
                    role="alert"
                    className="min-w-0 flex-1 text-body-xs text-destructive"
                  >
                    {error}
                    {many === null ? null : (
                      <>
                        {' '}
                        <TextLink
                          onClick={() => {
                            reading.reviewInImport(many)
                          }}
                        >
                          Review them
                        </TextLink>
                      </>
                    )}
                  </p>
                )}
                <Button
                  type="button"
                  className="w-30"
                  onClick={() => {
                    void read()
                  }}
                >
                  Read message
                </Button>
              </div>
            </div>
            <div
              aria-hidden="true"
              className="flex flex-col items-center gap-2 text-body-xs text-muted-foreground-subtle max-lg:hidden"
            >
              <span className="w-px flex-1 bg-border-subtle" />
              or
              <span className="w-px flex-1 bg-border-subtle" />
            </div>
            <ContractNoteCard />
          </div>
        ) : stage.kind === 'reading' ? (
          <ReadingSteps
            text={stage.text}
            steps={steps}
            reached={stage.fill === null ? Math.min(reached, STEP_COUNT - 1) : reached}
            onCancel={backToMessage}
          />
        ) : sip ? (
          SIP_AVAILABLE ? (
            <SipForm entry={entry} formId={formId} />
          ) : (
            <NotAvailable thing="Recording a SIP" />
          )
        ) : (
          <>
            <TransactionForm
              entry={entry}
              formId={formId}
              onSubmit={(event) => {
                event.preventDefault()
                if (!entry.pending) save()
              }}
              flash={new Set(flash.fill?.fields ?? [])}
              fillKey={flash.key}
              notes={notes}
            />
            <div className="flex items-center justify-end gap-3">
              <Button
                type="button"
                variant="secondary"
                className="hover:bg-secondary"
                disabled={entry.pending}
                onClick={backToMessage}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                form={formId}
                aria-disabled={entry.pending}
                className="aria-disabled:cursor-progress aria-disabled:opacity-70"
              >
                {entry.pending ? 'Saving…' : entry.option.saveLabel}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

/** The mock's `pfb-chip`: inset grey, no edge; hover lights the text and the edge; on, raised. */
const chipSkin = (on: boolean) =>
  cn(
    'pr-3 pl-2',
    on ? 'border-border-strong bg-secondary' : 'border-transparent bg-muted hover:border-border',
  )

/** One step of the read, in the mock's words (`Ne`). */
const readSteps = (fill: TradeFill | null, adDate: string): readonly ReadStep[] => [
  {
    title: 'Company',
    done:
      fill === null
        ? ''
        : fill.company !== null
          ? companyLabel(fill.company).replace(' · ', ', ')
          : fill.unmatched === null
            ? 'Not in the message'
            : `“${fill.unmatched}” not matched`,
  },
  {
    title: 'Type',
    done: fill === null || fill.kind === null ? 'Buy' : kindOption(fill.kind).label,
  },
  {
    title: 'Kitta and rate',
    done: fill === null ? '' : `${fill.quantity ?? '?'} at ${fill.price ?? fill.amount ?? '?'}`,
  },
  { title: 'Charges', done: rateTableLabel(fill?.date?.ad ?? adDate) },
]

/** One reading step's length, read from the token so the page and the CSS agree. */
const stepMs = (): number => {
  const raw = getComputedStyle(document.documentElement).getPropertyValue('--motion-read-step')
  const value = Number.parseInt(raw, 10)
  return Number.isNaN(value) ? 620 : value
}
